import { useState, useEffect, forwardRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowRight } from "lucide-react";
import { ScreenshotUploader } from "./ScreenshotUploader";
import { trackEvent } from "../lib/useAnalytics";

const PLACEHOLDERS = {
  profile:
    "Paste their dating profile, bio, or About Me here. Wingman will tell you if it feels real or scripted.",
  conversation:
    "Paste the chat here. Remove names if you want. Wingman will read the subtext for you.",
  full:
    "Paste the profile and the chat. Wingman will tell you if it feels real, what they actually mean, and what to say next.",
  matched:
    "Tell Wingman what happened. Example: \u201CWe matched on Facebook Dating. She liked me first. We haven\u2019t messaged yet.\u201D You can also paste profile or chat text, or upload a screenshot.",
};

const MODE_LABELS = {
  profile: { short: "Profile", long: "Check This Profile" },
  conversation: { short: "Chat", long: "Read This Chat" },
  full: { short: "Next Move", long: "Get My Next Move" },
  matched: { short: "We Matched", long: "We Matched \u2014 Now What?" },
};

const PLATFORMS = [
  { id: "facebook_dating", label: "Facebook Dating" },
  { id: "bumble", label: "Bumble" },
  { id: "hinge", label: "Hinge" },
  { id: "tinder", label: "Tinder" },
  { id: "ourtime", label: "OurTime" },
  { id: "match", label: "Match" },
  { id: "other", label: "Other" },
];

export const Analyzer = forwardRef(function Analyzer(
  { value, mode, onChange, onModeChange, onSubmit, loading, error, platform, onPlatformChange },
  ref,
) {
  const [text, setText] = useState(value || "");

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const handleChange = (next) => {
    setText(next);
    onChange(next);
  };

  const appendExtracted = (extracted, prefix = "") => {
    const cur = (text || "").trim();
    const body = prefix ? `${prefix}\n${extracted}` : extracted;
    handleChange(cur ? `${cur}\n\n${body}` : body);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === "matched" && mode !== "matched") {
      trackEvent("we_matched_started");
    }
    onModeChange(nextMode);
  };

  const handlePlatformClick = (id) => {
    onPlatformChange?.(id);
    trackEvent("match_platform_selected", { platform_source: id });
  };

  const matchedReady = mode !== "matched" || !!platform;
  const submitDisabled = loading || text.trim().length === 0 || !matchedReady;

  return (
    <section
      ref={ref}
      data-testid="analyzer-section"
      className="px-5 sm:px-8 py-12 max-w-3xl mx-auto"
    >
      <div className="ss-rule mb-6" data-testid="analyzer-eyebrow">
        <span>Get a read</span>
      </div>

      <h2
        className="ss-display-tight text-3xl sm:text-4xl mb-3"
        style={{ color: "var(--ss-ink)" }}
        data-testid="analyzer-title"
      >
        What are we looking at?
      </h2>
      <p
        className="text-base sm:text-lg mb-8"
        style={{ color: "var(--ss-muted)" }}
      >
        Pick the kind of moment. Paste it in. Get a clear, calm read.
      </p>

      <Tabs value={mode} onValueChange={handleModeChange} className="w-full">
        <TabsList
          data-testid="analyzer-tabs"
          className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto p-1 rounded-xl gap-0 border"
          style={{
            background: "var(--ss-bg-deep)",
            borderColor: "var(--ss-rule-line)",
          }}
        >
          {["profile", "conversation", "full", "matched"].map((m) => (
            <TabsTrigger
              key={m}
              value={m}
              data-testid={`tab-${m}`}
              className="tab-trigger text-[13px] sm:text-sm py-3 px-1 sm:px-2 rounded-lg leading-tight"
            >
              <span className="sm:hidden">{MODE_LABELS[m].short}</span>
              <span className="hidden sm:inline">{MODE_LABELS[m].long}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {["profile", "conversation", "full", "matched"].map((m) => (
          <TabsContent key={m} value={m} className="mt-5">
            {m === "matched" && (
              <div className="mb-5" data-testid="platform-picker">
                <p
                  className="ss-eyebrow mb-2.5"
                  style={{ color: "var(--ss-gold-deep)" }}
                >
                  Where did you match?
                </p>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => {
                    const active = platform === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handlePlatformClick(p.id)}
                        data-testid={`platform-${p.id}`}
                        aria-pressed={active}
                        className="px-3.5 py-2 rounded-full text-sm font-medium border transition-colors"
                        style={{
                          background: active ? "var(--ss-gold)" : "var(--ss-card-2)",
                          borderColor: active ? "var(--ss-gold-deep)" : "var(--ss-rule-line)",
                          color: active ? "#ffffff" : "var(--ss-ink-2)",
                        }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                {!platform && (
                  <p
                    className="mt-2 text-xs"
                    style={{ color: "var(--ss-muted)" }}
                    data-testid="platform-picker-hint"
                  >
                    Pick where you matched before Wingman reads it.
                  </p>
                )}
              </div>
            )}

            <div
              className="ss-panel p-1.5"
              style={{ borderColor: "var(--ss-rule-line)" }}
            >
              <textarea
                data-testid={`analyzer-textarea-${m}`}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={PLACEHOLDERS[m]}
                className="w-full min-h-[260px] sm:min-h-[300px] p-5 sm:p-6 text-base sm:text-lg leading-relaxed rounded-lg outline-none resize-y"
                style={{
                  background: "var(--ss-card-inset)",
                  color: "var(--ss-ink)",
                  border: "none",
                  fontFamily: "Raleway, sans-serif",
                }}
                maxLength={20000}
              />
            </div>
            <p
              className="mt-2 text-sm ss-mono"
              style={{ color: "var(--ss-muted)" }}
              data-testid={`analyzer-charcount-${m}`}
            >
              {text.length.toLocaleString()} / 20,000 characters
            </p>

            {m === "profile" && (
              <ScreenshotUploader
                testId="ocr-profile"
                label="Upload Screenshot"
                onExtracted={(t) => appendExtracted(t)}
              />
            )}

            {m === "conversation" && (
              <ScreenshotUploader
                testId="ocr-conversation"
                label="Upload Screenshot"
                onExtracted={(t) => appendExtracted(t)}
              />
            )}

            {m === "full" && (
              <div className="space-y-2">
                <ScreenshotUploader
                  testId="ocr-full-profile"
                  label="Upload profile screenshot(s)"
                  onExtracted={(t) => appendExtracted(t, "PROFILE:")}
                />
                <ScreenshotUploader
                  testId="ocr-full-conversation"
                  label="Upload conversation screenshot(s)"
                  onExtracted={(t) => appendExtracted(t, "CONVERSATION:")}
                />
              </div>
            )}

            {m === "matched" && (
              <ScreenshotUploader
                testId="ocr-matched"
                label="Upload match / chat screenshot (optional)"
                onExtracted={(t) => appendExtracted(t)}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>

      {error && (
        <div
          data-testid="analyzer-error"
          className="mt-5 p-4 rounded-xl font-semibold flex items-start gap-3"
          style={{
            background: "var(--ss-red-bg)",
            borderLeft: "4px solid var(--ss-red)",
            color: "var(--ss-ink)",
          }}
          role="alert"
        >
          <span style={{ color: "var(--ss-red)" }}>!</span>
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <button
          data-testid="analyzer-submit-btn"
          onClick={onSubmit}
          disabled={submitDisabled}
          className="ss-cta"
        >
          {loading ? "Reading..." : "Get the Read"}
          {!loading && <ArrowRight className="w-5 h-5" strokeWidth={2.25} />}
        </button>

        {loading && (
          <div
            data-testid="analyzer-loading"
            className="ss-serif inline-flex items-center gap-2 px-2 py-3 text-base"
            style={{ color: "var(--ss-muted)" }}
          >
            <span className="ss-pulse inline-block w-2 h-2 rounded-full" style={{ background: "var(--ss-gold)" }} />
            <span className="ss-pulse inline-block w-2 h-2 rounded-full" style={{ background: "var(--ss-gold)" }} />
            <span className="ss-pulse inline-block w-2 h-2 rounded-full" style={{ background: "var(--ss-gold)" }} />
            <span className="ml-2 text-sm" style={{ color: "var(--ss-ink-2)" }}>
              Looking it over&hellip;
            </span>
          </div>
        )}
      </div>
    </section>
  );
});
